import { CONTRACTOR_PAGES } from "../../pages/Application/contractorConstants";
import {
  PROTOCOL_DOCUMENT_PRINT_STYLES,
  escapeHtml,
  formatDate,
  signatureBlock,
} from "./jobDescriptionPrint";

const CONTRACTOR_PRINT_EXTRA_STYLES = `
  .cov-page { margin-bottom: 8px; }
  .cov-page + .cov-page { page-break-before: always; padding-top: 16px; }
  .cov-page-banner {
    text-align: center;
    padding: 10px 12px;
    margin-bottom: 14px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
  }
  .cov-page-banner h2 { margin: 0; font-size: 12px; font-weight: 800; color: #1e3a8a; text-transform: uppercase; letter-spacing: 0.06em; }
  .cov-page-banner p { margin: 4px 0 0 0; font-size: 10px; color: #1d4ed8; }
  .cov-section { margin-bottom: 14px; }
  .cov-section > h3 {
    font-size: 12px; font-weight: 700; color: #111827;
    border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; margin: 0 0 10px 0;
  }
  .cov-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 14px;
  }
  @media print {
    .cov-grid { grid-template-columns: 1fr 1fr; }
  }
  @media screen and (max-width: 640px) {
    .cov-grid { grid-template-columns: 1fr; }
  }
  .cov-table-wrap { overflow: hidden; border: 1px solid #e5e7eb; border-radius: 8px; margin: 10px 0; }
  .cov-table { width: 100%; border-collapse: collapse; font-size: 9px; }
  .cov-table th, .cov-table td { border: 1px solid #e5e7eb; padding: 6px 5px; text-align: center; }
  .cov-table th { background: #f3f4f6; font-weight: 700; color: #374151; }
  .cov-table td:first-child, .cov-table th:first-child { text-align: left; font-weight: 600; }
  .cov-rate { font-variant-numeric: tabular-nums; }
`;

/**
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseContractorFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  /** @type {Record<string, unknown>|null} */
  let agreement = null;
  if (Array.isArray(result)) {
    agreement = result.length > 0 ? result[0] : null;
  } else if (result?.agreement && typeof result.agreement === "object") {
    agreement = result.agreement;
  } else if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    agreement =
      result.items.find((item) => item.userId === uid) ||
      result.items[0] ||
      null;
  } else if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.contractorName != null ||
      result.userId != null)
  ) {
    agreement = result;
  }
  if (!agreement || typeof agreement !== "object") return null;
  return normalizeContractorAgreement(/** @type {Record<string, unknown>} */ (agreement));
}

/**
 * @param {Record<string, unknown>} agreement
 */
function normalizeContractorAgreement(agreement) {
  const out = { ...agreement };

  if (!out.serviceCounties && agreement.geographicAreaDescription) {
    out.serviceCounties = agreement.geographicAreaDescription;
  }

  if (!out.serviceAgreementDate && agreement.agreementDate) {
    out.serviceAgreementDate = agreement.agreementDate;
  }
  if (!out.effectiveDate && agreement.startDate) {
    out.effectiveDate = agreement.startDate;
  }

  if (!out.applicantSignature && agreement.contractorAuthorizedOfficialName) {
    out.applicantSignature = agreement.contractorAuthorizedOfficialName;
  }
  if (!out.applicantSignedAt && agreement.contractorDateSigned) {
    out.applicantSignedAt = agreement.contractorDateSigned;
  }
  if (!out.reviewedBySignature && agreement.agencyAuthorizedOfficialName) {
    out.reviewedBySignature = agreement.agencyAuthorizedOfficialName;
  }
  if (!out.reviewedBySignedAt && agreement.agencyDateSigned) {
    out.reviewedBySignedAt = agreement.agencyDateSigned;
  }
  if (!out.agencySignatureTitle && agreement.agencyTitle) {
    out.agencySignatureTitle = agreement.agencyTitle;
  }

  const rates = agreement.serviceRates;
  if (Array.isArray(rates)) {
    rates.forEach((r) => {
      if (!r || typeof r !== "object") return;
      const d = /** @type {{ discipline?: string; serviceType?: string; rate?: unknown }} */ (r);
      if (d.discipline && d.serviceType != null) {
        out[`rate_${d.discipline}_${d.serviceType}`] = d.rate;
      }
    });
  }

  return out;
}

/**
 * @param {Record<string, unknown>} record
 * @param {string} name
 * @param {string} [type]
 */
function displayField(record, name, type) {
  const raw = record[name];
  if (raw == null || raw === "") return "—";
  if (type === "date") {
    const d = formatDate(raw);
    return d ? escapeHtml(d) : escapeHtml(String(raw));
  }
  return escapeHtml(String(raw));
}

/**
 * @param {Record<string, unknown>} record
 * @param {Record<string, unknown>} section
 */
function sectionToHtml(record, section) {
  switch (section.type) {
    case "header_inputs":
    case "inputs_group": {
      const title = section.content
        ? `<h3>${escapeHtml(section.content)}</h3>`
        : "";
      const fields = (section.fields || [])
        .map(
          (field) => `
        <div>
          <div class="sig-label">${escapeHtml(field.label || field.name)}</div>
          <div class="field-value">${displayField(record, field.name, field.type)}</div>
        </div>`,
        )
        .join("");
      return `<div class="cov-section">${title}<div class="cov-grid">${fields}</div></div>`;
    }
    case "text_block": {
      const t = section.title
        ? `<h2 class="jd-h2">${escapeHtml(section.title)}</h2>`
        : "";
      const c = section.content
        ? `<p class="jd-prose" style="white-space:pre-wrap">${escapeHtml(section.content)}</p>`
        : "";
      return `<div class="jd-block">${t}${c}</div>`;
    }
    case "list": {
      const items = (section.items || [])
        .map((item) => `<li>${escapeHtml(item)}</li>`)
        .join("");
      return `<ul class="jd-ul">${items}</ul>`;
    }
    case "rates_table": {
      const disciplines = section.disciplines || [];
      const columns = section.columns || [];
      const head =
        `<tr><th>Discipline</th>` +
        columns
          .map((col) => `<th>${escapeHtml(col.label || col.key)}</th>`)
          .join("") +
        "</tr>";
      const body = disciplines
        .map((disc) => {
          const cells = columns
            .map((col) => {
              const key = `rate_${disc.key}_${col.key}`;
              const v = record[key];
              const disp =
                v != null && v !== "" && !Number.isNaN(Number(v))
                  ? `$${escapeHtml(String(v))}`
                  : "—";
              return `<td class="cov-rate">${disp}</td>`;
            })
            .join("");
          return `<tr><td>${escapeHtml(disc.label || disc.key)}</td>${cells}</tr>`;
        })
        .join("");
      const cap = section.title
        ? `<p class="jd-muted" style="margin:0 0 8px 0;font-weight:600">${escapeHtml(section.title)}</p>`
        : "";
      return `${cap}<div class="cov-table-wrap"><table class="cov-table"><thead>${head}</thead><tbody>${body}</tbody></table></div>`;
    }
    default:
      return "";
  }
}

/**
 * @param {Record<string, unknown>} record
 */
function buildAgreementBodyHtml(record) {
  return CONTRACTOR_PAGES.map((page, pi) => {
    const sections = (page.sections || [])
      .map((sec, si) => sectionToHtml(record, sec))
      .join("");
    const banner = `<div class="cov-page-banner">
      <h2>${escapeHtml(page.title || "Contractor service agreement")}</h2>
      <p>Page ${pi + 1} of ${CONTRACTOR_PAGES.length}</p>
    </div>`;
    return `<div class="cov-page">${banner}${sections}</div>`;
  }).join("");
}

/**
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildContractorServiceAgreementPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const contractorName = escapeHtml(
    String(record.contractorName || record.applicantName || "").trim(),
  );

  const applicantSig = record.applicantSignature;
  const applicantSignedAt = escapeHtml(
    formatDate(record.applicantSignedAt || record.applicantSignatureDate),
  );
  const reviewedBySignedBy = escapeHtml(
    String(record.reviewedBySignedBy || ""),
  );
  const agencyTitle = escapeHtml(
    String(record.agencySignatureTitle || record.agencyTitle || "").trim(),
  );
  const reviewedBySig =
    record.reviewedBySignature || record.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      record.reviewedBySignedAt ||
        record.adminSignatureDate ||
        record.reviewedBySignatureDate,
    ),
  );

  const bodyHtml = buildAgreementBodyHtml(record);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Contractor service agreement — ${contractorName || "Contractor"}</title>
  <style>${PROTOCOL_DOCUMENT_PRINT_STYLES}${CONTRACTOR_PRINT_EXTRA_STYLES}</style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Contractor service agreement</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>

    ${bodyHtml}

    <div class="two-col">
      <div class="sig-card">
        <h3>Contractor</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${contractorName || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Agency representative</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Title</div>
        <div class="field-value">${agencyTitle || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
